import React, { useContext, useState } from "react"
import { Divider, Form } from "semantic-ui-react"
import { CAMPING_STYLES, PREREQUISITES } from "~/constants"
import { CampingStyleProps, CampProps, PrerequisteProps } from "../../interfaces"
import { validElectric, validRigType } from "~/types"
import MaxRigLengthField from "./prerequisites/MaxRigLengthField"
import ElectricField from "./prerequisites/ElectricField"
import RigTypeField from "./prerequisites/RigTypeField"
import PassengersField from "./prerequisites/PassengersField"
import { BookerContext } from "~/contexts/BookerContext"
import PetsField from "./prerequisites/PetsField"
import YearField from "./prerequisites/YearField"
import BookerButton from "../ui/BookerButton"
import { getYearDifference } from "~/utils/booker"
import { rigLabel } from "~/utils/rigLabel"

interface ComponentProps {
  camp: CampProps
  campingStyle: CampingStyleProps
  prerequisites: PrerequisteProps[]
  setUserRigType
  setUserRigLength
  setUserAmps
  setUserPassengers: (v: number) => void
  setUserKidCampers: (v: number) => void
  setShowPrerequisites
  userPassengers: number
  userRigLength: number
  userAmps: validElectric
  userKidCampers: number
  userPets: number
}

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  hex = hex.replace(/^#/, "")

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("")
  }

  const num = parseInt(hex, 16)
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  }
}

export const getTextColorBasedOnBackground = (hexColor: string): string => {
  const { r, g, b } = hexToRgb(hexColor)

  const brightness = 0.299 * r + 0.587 * g + 0.114 * b

  return brightness > 234 ? "#000000" : "#FFFFFF"
}

interface IErrorType {
  [PREREQUISITES.ELECTRIC]: string
  [PREREQUISITES.MAX_RIG_LENGTH]: string
  [PREREQUISITES.PASSENGERS]: string
  [PREREQUISITES.PETS]: string
  [PREREQUISITES.RIG_TYPE]: string
  [PREREQUISITES.RIG_YEAR]: string
  kids: string
}

const initErrors: IErrorType = {
  [PREREQUISITES.ELECTRIC]: "",
  [PREREQUISITES.MAX_RIG_LENGTH]: "",
  [PREREQUISITES.PASSENGERS]: "",
  [PREREQUISITES.PETS]: "",
  [PREREQUISITES.RIG_TYPE]: "",
  [PREREQUISITES.RIG_YEAR]: "",
  kids: "",
}

const Prerequisites = (props: ComponentProps) => {
  const bookerContext = useContext(BookerContext)
  const { camp, campingStyle, secondaryColor, isKidsPrereqPresent } = bookerContext

  const isForBoat = bookerContext.campingStyle.name === CAMPING_STYLES.BOAT_SLIP
  const siteType = isForBoat ? "boat slip" : "site"

  const [userRigType, setUserRigType] = useState<validRigType>()
  const [userRigLength, setUserRigLength] = useState(props.userRigLength)
  const [userAmps, setUserAmps] = useState<validElectric>(props.userAmps)
  const [userPassengers, setUserPassengers] = useState<number>(props.userPassengers || 1)
  const [userKidCampers, setUserKidCampers] = useState<number>(props.userKidCampers || 0)
  const [userPets, setUserPets] = useState<number>(props.userPets || 0)
  const [userRigYear, setUserRigYear] = useState<number>()
  const [errors, setErrors] = useState<IErrorType>(initErrors)
  
  const requiredFields = props.prerequisites.map((pr) => pr.kind)

  const handleRigTypeChange = (e, { value }) => setUserRigType(value)
  const handleRigLengthChange = (e, { value }) => setUserRigLength(value)
  const handleAmpsChange = (e, { value }) => setUserAmps(value)
  const handlePassengersChange = (e, { value }) => setUserPassengers(value)
  const handleKidsCampersChange = (e, { value }) => setUserKidCampers(value)

  const handleSubmit = () => {
    if (!isValid()) return
    if (requiredFields.includes(PREREQUISITES.RIG_TYPE)) props.setUserRigType(userRigType)
    if (requiredFields.includes(PREREQUISITES.ELECTRIC)) props.setUserAmps(userAmps)
    if (requiredFields.includes(PREREQUISITES.MAX_RIG_LENGTH)) props.setUserRigLength(userRigLength)
    if (requiredFields.includes(PREREQUISITES.PASSENGERS)) props.setUserPassengers(userPassengers)
    if (requiredFields.includes(PREREQUISITES.PASSENGERS) && isKidsPrereqPresent) {
      props.setUserKidCampers(userKidCampers)
    }
    if (requiredFields.includes(PREREQUISITES.PETS)) bookerContext.setUserPets(userPets)
    if (requiredFields.includes(PREREQUISITES.RIG_YEAR)) bookerContext.setUserRigYear(userRigYear)

    props.setShowPrerequisites(false)
  }

  const isValid = () => {
    let hasErrors = false
    const newErrors: IErrorType = { ...initErrors }

    if (requiredFields.includes(PREREQUISITES.RIG_TYPE) && !userRigType) {
      newErrors[PREREQUISITES.RIG_TYPE] = `Please select the type of ${rigLabel(isForBoat, camp.is_camper_length, camp.length_unit)}.`
      hasErrors = true
    }
    if (requiredFields.includes(PREREQUISITES.MAX_RIG_LENGTH) && !userRigLength) {
      newErrors[PREREQUISITES.MAX_RIG_LENGTH] = `Please specify the maximum length of your ${rigLabel(isForBoat, camp.is_camper_length, camp.length_unit)}.`
      hasErrors = true
    }
    if (requiredFields.includes(PREREQUISITES.ELECTRIC) && !userAmps) {
      newErrors[PREREQUISITES.ELECTRIC] = "Please select the electric amperage."
      hasErrors = true
    }
    if (requiredFields.includes(PREREQUISITES.PASSENGERS) && !userPassengers) {
      newErrors[PREREQUISITES.PASSENGERS] = "Please enter the number of passengers."
      hasErrors = true
    }
    if (requiredFields.includes(PREREQUISITES.PASSENGERS) && isKidsPrereqPresent && userKidCampers === null) {
      newErrors.kids = "Please enter the number of childrens."
      hasErrors = true
    }
    if (requiredFields.includes(PREREQUISITES.PETS) && !userPets && !Number.isInteger(userPets)) {
      newErrors[PREREQUISITES.PETS] = "Please enter the number of pets."
      hasErrors = true
    }
    if (requiredFields.includes(PREREQUISITES.RIG_YEAR)) {
      if (!userRigYear && campingStyle.max_rig_age == null) {
        newErrors[PREREQUISITES.RIG_YEAR] = "Please enter the year of your vehicle."
        hasErrors = true
      } else if (campingStyle.max_rig_age !== null && getYearDifference(userRigYear) > campingStyle.max_rig_age) {
        newErrors[PREREQUISITES.RIG_YEAR] =
          `For vehicles older than ${campingStyle.max_rig_age} years, please call us at ${camp.phone}`
        hasErrors = true
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }))

    return !hasErrors
  }

  return (
    <div className="max-w-2xl mx-auto my-0" style={{ marginBottom: 65 }}>
      <Divider hidden />
      <p className="text-lg pt-2 pb-3 font-bold">Tell us a bit more</p>
      {/* <p>
          Please provide the following information so we can find you the perfect {siteType} for your setup.
        </p> */}

      <Divider hidden />

      <Form className="flex flex-col gap-3">
        {requiredFields.includes(PREREQUISITES.RIG_TYPE) && (
          <RigTypeField
            setError={(v: string) => setErrors((prev) => ({ ...prev, [PREREQUISITES.RIG_TYPE]: v }))}
            error={errors[PREREQUISITES.RIG_TYPE]}
            userRigType={userRigType}
            handleRigTypeChange={handleRigTypeChange}
          />
        )}
        {requiredFields.includes(PREREQUISITES.ELECTRIC) && (
          <ElectricField
            setError={(v: string) => setErrors((prev) => ({ ...prev, [PREREQUISITES.ELECTRIC]: v }))}
            error={errors[PREREQUISITES.ELECTRIC]}
            sites={props.camp.sites}
            userAmps={userAmps}
            handleAmpsChange={handleAmpsChange}
          />
        )}
        {requiredFields.includes(PREREQUISITES.MAX_RIG_LENGTH) && (
          <MaxRigLengthField
            setError={(v: string) => setErrors((prev) => ({ ...prev, [PREREQUISITES.MAX_RIG_LENGTH]: v }))}
            error={errors[PREREQUISITES.MAX_RIG_LENGTH]}
            userRigLength={userRigLength}
            handleRigLengthChange={handleRigLengthChange}
            isBoat={isForBoat}
            isCamperLength={props.camp.is_camper_length}
            sites={campingStyle.sites}
            lengthUnit={props.camp.length_unit}
          />
        )}
        {requiredFields.includes(PREREQUISITES.RIG_YEAR) && (
          <YearField
            setError={(v: string) => setErrors((prev) => ({ ...prev, [PREREQUISITES.RIG_YEAR]: v }))}
            error={errors[PREREQUISITES.RIG_YEAR]}
            userRigYear={userRigYear}
            setUserRigYear={setUserRigYear}
            isCamperLength={props.camp.is_camper_length}
          />
        )}
        {requiredFields.includes(PREREQUISITES.PASSENGERS) && (
          <PassengersField
            setAdultsError={(v: string) => setErrors((prev) => ({ ...prev, [PREREQUISITES.PASSENGERS]: v }))}
            setKidError={(v: string) => setErrors((prev) => ({ ...prev, kids: v }))}
            adultsError={errors[PREREQUISITES.PASSENGERS]}
            kidError={errors.kids}
            camp={props.camp}
            userPassengers={userPassengers}
            handlePassengersChange={handlePassengersChange}
            userKidCampers={userKidCampers}
            handleKidCampersChange={handleKidsCampersChange}
          />
        )}
        {requiredFields.includes(PREREQUISITES.PETS) && (
          <PetsField
            setError={(v: string) => setErrors((prev) => ({ ...prev, [PREREQUISITES.PETS]: v }))}
            error={errors[PREREQUISITES.PETS]}
            camp={camp}
            userPets={userPets}
            setUserPets={setUserPets}
            petsCapacity={campingStyle.pets_capacity}
          />
        )}
        <div
          className="flex justify-end fixed w-full left-0 bottom-0 px-5 py-3.5 bg-white border-t Z-50 items-center"
          style={{ borderColor: "rgba(34, 36, 38, .15)" }}
        >
          <BookerButton
            disabled={Object.values(errors).some((error) => error)}
            onClick={handleSubmit}
            className={"relative"}
            secondaryColor={secondaryColor}
            buttonText={"Next"}
            dataTestId="confirm-prerequisites"
          />
        </div>
      </Form>
    </div>
  )
}

export default Prerequisites
